import React, { useCallback, useState } from 'react';
import { Provider, ReactReduxContext } from 'react-redux';
import GfModal from '../gfcomponents/GfModal/GfModal';
import UserGroupDropdown from './UserGroupDropdown';
import './TransferModal.scss';
import { Form } from 'semantic-ui-react';
import { canIUse, Features } from '../helpers/PermissionUtility';
import { analyticsTrack } from '../helpers/AnalyticsUtility';

export interface TransferModalProps {
  currentOwnerId?: string;
  isTransferring?: boolean;
  bulkTransfer?: boolean;
  allowComments?: boolean;
  transferForms?: (item: string, transferType: number, comments?: string) => Promise<void>;
  toggleTransferModal?: VoidFunction;
}

export type TransferModalState = {
  userPage: number;
  searchInput: string;
  typingTimeout: number;
  showSearchResults: boolean;
  selectedItem?: string;
  transferType?: number;
  selectedId?: string;
  selectedName?: string;
  comments?: string;
};

export default function TransferModal(props: TransferModalProps) {
  const { bulkTransfer, isTransferring, toggleTransferModal, transferForms } = props;
  const [modalState, setModalState] = useState<TransferModalState>({
    userPage: 1,
    searchInput: '',
    typingTimeout: 0,
    showSearchResults: false
  });

  const handleClose = useCallback(() => {
    if (modalState.typingTimeout) {
      clearTimeout(modalState.typingTimeout);
    }

    toggleTransferModal();
  }, [modalState.typingTimeout]);

  const handleTransferClick = useCallback(async () => {
    if (modalState.selectedItem) {
      if (!!modalState.comments) {
        analyticsTrack('Transfer Form - Comment Added');
      }

      await transferForms(modalState.selectedItem, modalState.transferType, modalState.comments);
    }

    handleClose();
  }, [modalState.selectedItem, modalState.transferType, modalState.comments]);

  const onChange = useCallback(
    (id: string, name: string, type: 'user' | 'group') => {
      let transferType = -1;
      if (type === 'user') {
        transferType = 0;
      } else if (type === 'group') {
        transferType = 1;
      }

      setModalState({ ...modalState, selectedItem: id, selectedId: id, selectedName: name, transferType });
    },
    [modalState]
  );

  return (
    <ReactReduxContext.Consumer>
      {(ctx) => (
        <Provider store={ctx.store}>
          <GfModal
            className="transfer-form-modal"
            title={bulkTransfer ? 'Transfer Forms' : 'Transfer Form'}
            primaryText="Transfer"
            tertiaryText="Cancel"
            onClose={handleClose}
            onPrimaryClick={handleTransferClick}
            onTertiaryClick={handleClose}
            loading={isTransferring}
            actionButtonProps={{ primary: { disabled: !modalState.selectedItem } }}
          >
            <UserGroupDropdown
              onChange={onChange}
              selectedId={modalState.selectedId}
              selectedName={modalState.selectedName}
              hideAdministrator={true}
              hideDeactivatedUsers={true}
              hideCurrentUser={true}
              hidePendingUsers={true}
              groupSelected={modalState.transferType === 1}
            />
            {canIUse(Features.TransferComments) && !!props.allowComments && (
              <div className="comments-container">
                <label className="comments-header">Leave a comment (Optional)</label>
                <Form>
                  <textarea
                    className="comments"
                    value={modalState.comments}
                    onChange={(e) => setModalState({ ...modalState, comments: e.target.value })}
                    maxLength={400}
                  />
                </Form>
                <span className="character-count">{`${modalState.comments?.length || 0}/400 characters`}</span>
              </div>
            )}
          </GfModal>
        </Provider>
      )}
    </ReactReduxContext.Consumer>
  );
}
